import _ from "lodash";
import React, { useRef, useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

import CFPreviewCard from "../shared/CFPreviewCard";
import CFCollapse from "../shared/CFCollapse";
import CFCheckbox from "../shared/forms/CFCheckbox";
import { closeBodyModalOpen } from "../../utils/builderUtils";

const TemplatesModal = (props) => {
  const {
    title = "Templates",
    templates = [],
    isOpen,
    onClose,
    onSelectTemplate,
    website,
    selectedItem,
    filterConfig = {
      showOfficialFilter: true,
      showMyTemplatesFilter: true,
      showStyleFilters: true,
      showTagFilters: true,
      customFilters: []
    },
    templateCardProps = {
      imageUrlKey: 'screenshot',
      titleKey: 'name',
      buttonLabel: 'Use Template'
    },
    initialFilters = null,
    hideTemplatesList = false
  } = props;

  const defaultFilters = {
    official: false,
    myTemplates: false,
  }
  const normalizeTagName = (tagName) =>
    String(tagName || "").trim().toLowerCase();

  const modalRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [tagResetKey, setTagResetKey] = useState(0);
  const [templateTypeFilters, setTemplateTypeFilters] = useState(defaultFilters);
  const [selectedTags, setSelectedTags] = useState({});
  const [customFilters, setCustomFilters] = useState({});

  // Filter out current template if replacing
  let availableTemplates = templates;
  if (selectedItem?.template) {
    availableTemplates = templates.filter(temp => temp.id !== selectedItem.template.id);
  }

  const { officialTemplatesCount, myTemplatesCount, individualTagCounts, lightTemplatesCount, darkTemplatesCount } = useMemo(() => {
    const officialCount = availableTemplates.filter((t) => t.official).length;
    const myCount = availableTemplates.filter((t) =>
      t.accounts?.find((acc) => acc.id === website?.account_id)
    ).length;

    const lightCount = availableTemplates.filter((t) => t.name.toLowerCase().includes('light')).length;
    const darkCount = availableTemplates.filter((t) => t.name.toLowerCase().includes('dark')).length;

    const tagCountsMap = {};
    availableTemplates.forEach((temp) => {
      (temp.template_tags || []).forEach((tagDetail) => {
        const groupName = tagDetail.tag_type;
        if (!tagCountsMap[groupName]) {
          tagCountsMap[groupName] = {};
        }
        if (!tagCountsMap[groupName][tagDetail.name]) {
          tagCountsMap[groupName][tagDetail.name] = 0;
        }
        tagCountsMap[groupName][tagDetail.name]++;
      });
    });
    return {
      officialTemplatesCount: officialCount,
      myTemplatesCount: myCount,
      individualTagCounts: tagCountsMap,
      lightTemplatesCount: lightCount,
      darkTemplatesCount: darkCount,
    };
  }, [availableTemplates, website?.account_id]);

  const updateSearchText = _.debounce((value) => {
    setSearchText(value);
    setSelectedTags({});
    setTemplateTypeFilters(defaultFilters);
    setCustomFilters({});
    setTagResetKey(prev => prev + 1);
  }, 300);

  const filteredTemplates = useMemo(() => {
    let result = availableTemplates;
    const unifiedSelectedTags = Object.values(selectedTags).reduce((prev, curr) => ({ ...prev, ...curr }), {});
    const filteredTags = Object.keys(unifiedSelectedTags).filter((key) => unifiedSelectedTags[key]);
    const normalizedFilteredTags = new Set(
      filteredTags.map(normalizeTagName).filter(Boolean)
    );

    // First apply search text filter
    if (searchText) {
      result = result.filter((template) => {
        return template.name.toLowerCase().includes(searchText.toLowerCase());
      });
    }

    // Then apply tag filters (compounding/OR logic within tags)
    if (normalizedFilteredTags.size > 0) {
      result = result.filter((template) => {
        return template.template_tags?.some((tag) =>
          normalizedFilteredTags.has(normalizeTagName(tag?.name))
        );
      });
    }

    // Then apply template type filters (filtering on top of tag results)
    const hasTemplateTypeFilters = templateTypeFilters.official || templateTypeFilters.myTemplates;
    if (hasTemplateTypeFilters) {
      result = result.filter((template) => {
        let matched = false;

        if (templateTypeFilters.official && template.official) {
          matched = true;
        }

        if (
          templateTypeFilters.myTemplates &&
          template.accounts?.find((account) => account.id === website?.account_id)
        ) {
          matched = true;
        }

        return matched;
      });
    }

    // Then apply style filters (filtering on top of previous results)
    const hasStyleFilters = templateTypeFilters.light || templateTypeFilters.dark;
    if (hasStyleFilters) {
      result = result.filter((template) => {
        let matched = false;

        if (templateTypeFilters.light && !template.name.toLowerCase().includes('dark')) {
          matched = true;
        }

        if (templateTypeFilters.dark && template.name.toLowerCase().includes('dark')) {
          matched = true;
        }

        return matched;
      });
    }

    // Finally apply custom filters (filtering on top of previous results)
    const activeCustomFilters = Object.entries(customFilters).filter(([key, value]) => value);
    if (activeCustomFilters.length > 0) {
      result = result.filter((template) => {
        return activeCustomFilters.some(([key, value]) => template[key] === value);
      });
    }

    return result;
  }, [availableTemplates, templateTypeFilters, selectedTags, searchText, customFilters]);

  const tagGroups = useMemo(() => {
    const tags = {};

    // Add tags from available templates
    availableTemplates.forEach((template) => {
      (template.template_tags || []).forEach((tag) => {
        const group = tag.tag_type;
        if (!tags[group]) tags[group] = new Set();
        tags[group].add(tag.name);
      });
    });

    // Also add tags from initialFilters to ensure section tags are always available
    if (initialFilters) {
      Object.entries(initialFilters).forEach(([group, groupTags]) => {
        if (groupTags && typeof groupTags === 'object') {
          if (!tags[group]) tags[group] = new Set();
          Object.keys(groupTags).forEach(tagName => {
            tags[group].add(tagName);
          });
        }
      });
    }

    return tags;
  }, [availableTemplates, initialFilters]);

  const closeCallback = () => {
    closeBodyModalOpen();

    onClose?.();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeCallback();
    }
  };

  useEffect(() => {
    if (!modalRef.current) return;

    if (initialFilters) {
      if (initialFilters.myTemplates === true) {
        setTemplateTypeFilters((f) => ({ ...f, myTemplates: true }));
        setSelectedTags({});
      } else {
        setTemplateTypeFilters(defaultFilters);

        const newSelectedTags = {};
        Object.entries(initialFilters).forEach(([tag_type, tags]) => {
          if (!tags || typeof tags !== "object") return;

          const availableTagsInGroup = Array.from(tagGroups[tag_type] || []);
          const normalizedToRealName = {};
          availableTagsInGroup.forEach((tagName) => {
            const normalizedTagName = normalizeTagName(tagName);
            if (!(normalizedTagName in normalizedToRealName)) {
              normalizedToRealName[normalizedTagName] = tagName;
            }
          });

          Object.entries(tags).forEach(([tag, value]) => {
            if (!newSelectedTags[tag_type]) {
              newSelectedTags[tag_type] = {};
            }
            const normalizedTag = normalizeTagName(tag);
            const resolvedTagName = normalizedToRealName[normalizedTag] || normalizedTag;
            newSelectedTags[tag_type][resolvedTagName] = value;
          });
        });
        setSelectedTags(newSelectedTags);
      }
    } else {
      setSelectedTags({});
      setTemplateTypeFilters(defaultFilters);
    }

    let parentModal = modalRef.current.closest(".modal");

    if (parentModal) {
      $(modalRef.current)
        .off("shown.bs.modal")
        .on("shown.bs.modal", function () {
          $(parentModal).animate({ scrollTop: 0 });
        });
    }

    $(modalRef.current)
      .off("hidden.bs.modal")
      .on("hidden.bs.modal", function () {
        closeCallback();
      });

    if (isOpen) {
      $(modalRef.current).modal("show");
      $(modalRef.current).addClass("in").css("display", "block");
      $(modalRef.current).parents(".modal").scrollTop(0);
    } else {
      $(modalRef.current).removeClass("in").css("display", "none");
    }
  }, [isOpen, initialFilters, tagGroups]);

  const renderFilters = () => (
    <div className="flex flex-column gap-4 p-3 mt-4 overflow-y-auto">
      {filterConfig.showOfficialFilter && (
        <CFCollapse
          text="Template Types"
          defaultOpen={true}
          className="flex flex-column gap-1"
        >
          <CFCheckbox
            label="Official"
            checked={templateTypeFilters.official}
            callback={(checked) =>
              setTemplateTypeFilters((f) => ({ ...f, official: checked }))
            }
            rightLabel={officialTemplatesCount}
          />
          {filterConfig.showMyTemplatesFilter && (
            <CFCheckbox
              label="My Templates"
              checked={templateTypeFilters.myTemplates}
              callback={(checked) =>
                setTemplateTypeFilters((f) => ({
                  ...f,
                  myTemplates: checked,
                }))
              }
              rightLabel={myTemplatesCount}
            />
          )}
        </CFCollapse>
      )}

      {filterConfig.showStyleFilters && (
        <CFCollapse
          text="Style"
          defaultOpen={true}
          className="flex flex-column gap-1"
        >
          <CFCheckbox
            label="Light"
            checked={templateTypeFilters.light}
            callback={(checked) =>
              setTemplateTypeFilters((f) => ({ ...f, light: checked }))
            }
            rightLabel={lightTemplatesCount}
          />

          <CFCheckbox
            label="Dark"
            checked={templateTypeFilters.dark}
            callback={(checked) =>
              setTemplateTypeFilters((f) => ({ ...f, dark: checked }))
            }
            rightLabel={darkTemplatesCount}
          />
        </CFCollapse>
      )}

      {filterConfig.showTagFilters && Object.entries(tagGroups)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([group, tags]) => (
          <CFCollapse
            key={group}
            text={humanizeStr(group)}
            defaultOpen={true}
            className="flex flex-column gap-1"
          >
            {[...tags]
              .sort((a, b) => a.localeCompare(b))
              .map((tagName) => (
                <CFCheckbox
                  key={`${tagName}-${tagResetKey}`}
                  label={tagName}
                  checked={
                    selectedTags &&
                    selectedTags[group] &&
                    selectedTags[group][tagName] === true
                  }
                  rightLabel={individualTagCounts[group]?.[tagName] || 0}
                  callback={(checked) => {
                    setSelectedTags((f) => ({
                      ...f,
                      [group]: {
                        ...f[group],
                        [tagName]: checked,
                      },
                    }));
                  }}
                />
              ))}
          </CFCollapse>
        ))}

      {filterConfig.customFilters?.map((filter) => (
        <CFCollapse
          key={filter.key}
          text={filter.label}
          defaultOpen={true}
          className="flex flex-column gap-1"
        >
          {filter.options.map((option) => (
            <CFCheckbox
              key={option.value}
              label={option.label}
              checked={customFilters[filter.key] === option.value}
              callback={(checked) => {
                setCustomFilters((f) => ({
                  ...f,
                  [filter.key]: checked ? option.value : null,
                }));
              }}
            />
          ))}
        </CFCollapse>
      ))}
    </div>
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeCallback();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const modalContent = (
    <div
      ref={modalRef}
      className="cf-templates-modal modal fade cf-modal-centered"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="myModalLabel"
      aria-modal="true"
      data-backdrop="false"
      style={{
        background: "rgb(0, 0, 0, 0.21)",
        overflow: "visible",
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-dialog !mt-10 !max-w-[1600px] !w-[95%] !h-[95vh] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content overflow-hidden remix-editor flex flex-col md:flex-row h-full">
          <div className="w-full md:w-1/4 lg:w-1/5 flex flex-col py-[25px] px-[20px] bg-slate-100">
            <h4 className="black margin-top-zero font-semibold">{title}</h4>

            <div
              className="w-full mt-2 relative border-solid border-0 border-slate-200"
              style={{ borderBottomWidth: "1px" }}
            >
              <span className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400">
                <i className="fas fa-search text-2xl"></i>
              </span>

              <input
                type="text"
                name="name"
                className="w-full px-2 py-4 pl-8 text-xl rounded-lg text-slate-500 dark:focus:color-white bg-transparent dark:bg-slate-950  border-none focus:border-none focus:outline-none"
                placeholder="Search..."
                defaultValue={searchText}
                onChange={(e) => updateSearchText(e.target.value)}
              />
            </div>

            {renderFilters()}
          </div>

          <div className="w-full md:w-3/4 lg:w-4/5 flex flex-col py-[25px] px-[20px]">
            <div className="flex justify-end">
              <button
                onClick={closeCallback}
                type="button"
                className="absolute close dark:text-white"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="20"
                  viewBox="0 0 17 17"
                  fill="none"
                >
                  <path
                    d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  ></path>
                </svg>
              </button>
            </div>

            <React.Fragment>
              <div className="overflow-y-auto h-full">
                {props.children && (
                  <div className="mb-6">
                    {props.children}
                  </div>
                )}

                {!hideTemplatesList && (
                  <div className="flex flex-wrap gap-4">
                    {filteredTemplates.length === 0 && (
                      <div className="text-center text-gray-500">
                        No templates found
                      </div>
                    )}

                    {filteredTemplates.sort((a, b) => b.times_used - a.times_used).map((item) => (
                      <div
                        key={item.id}
                        className="basis-[31.5%] 3xl:basis-[23.5%] overflow-hidden"
                      >
                        <CFPreviewCard
                          imageUrl={item[templateCardProps.imageUrlKey]}
                          title={item[templateCardProps.titleKey]}
                          className="h-[250px] 2xl:h-[300px] 3xl:h-[350px]"
                          buttonLabel={templateCardProps.buttonLabel}
                          onClick={() => {
                            onSelectTemplate(item);
                            closeCallback();
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {isOpen && createPortal(modalContent, document.body)}
    </React.Fragment>
  );
};

export default TemplatesModal;
